import { AddCircle, Delete } from "@mui/icons-material";
import {
  Box,
  IconButton,
  List,
  Typography,
  MenuItem,
  Menu,
} from "@mui/material";
import CompactListItem from "../CompactListItem/CompactListItem";
import { useEffect, useState } from "react";
import Tooltip from "../Tooltip/Tooltip";
import { SubRoleDetailedOverview } from "../../features/SubRolesPage/Models/SubRoleDetailedOverview";
import { NO_PRIVILEGES, NO_DESCRIPTION } from "../../utility/TextLabels";
import { Privilege } from "../../features/PrivilegesPage/Models/Privilige";

type AddFrameProps = {
  label: string;
  preSelectedItems?: string[];
  selectableItems: SubRoleDetailedOverview[] | Privilege[];
  onAddingItem: (itemName: string) => void;
  onRemovingItem: (itemName: string) => void;
};

const EditableCompactList: React.FC<AddFrameProps> = ({
  label,
  preSelectedItems,
  selectableItems,
  onAddingItem,
  onRemovingItem,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>(
    preSelectedItems ? preSelectedItems : []
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    setSelectedItems(preSelectedItems ? preSelectedItems : []);
  }, [preSelectedItems]);

  const labelStyle = {
    color: "black",
    fontFamily: "Lato",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleItemAdd = (item: string) => {
    setSelectedItems([...selectedItems, item]);
    onAddingItem(item);
    handleMenuClose();
  };

  const handleItemRemove = (itemToRemove: string) => {
    const updatedItems: string[] = selectedItems.filter(
      (item) => item !== itemToRemove
    );
    setSelectedItems(updatedItems);

    onRemovingItem(itemToRemove);
    handleMenuClose();
  };

  const generateTooltipText = (item: string) => {
    const privilegeDescription = (selectableItems as Privilege[])?.find(
      (privilege) => privilege.name === item
    )?.description;

    const subRolePrivileges = (
      selectableItems as SubRoleDetailedOverview[]
    )?.find((subrole) => subrole.name === item)?.privilegeDetailedOverview;

    if (privilegeDescription) {
      return [{ tooltipText: privilegeDescription }];
    }

    if (subRolePrivileges?.length) {
      return subRolePrivileges.map((detailedOverview) => ({
        tooltipTitle: detailedOverview?.privilegeName,
        tooltipText: detailedOverview?.privilegeDescription,
      }));
    }

    return [
      {
        tooltipText:
          subRolePrivileges?.length === 0 ? NO_PRIVILEGES : NO_DESCRIPTION,
      },
    ];
  };

  return (
    <>
      <Typography variant="h3" {...labelStyle}>
        {label}
      </Typography>
      {selectedItems && (
        <List sx={{ paddingBottom: 0 }}>
          {selectedItems.map((item, index) => (
            <CompactListItem
              key={index}
              start={index === 0}
              end={index === selectedItems.length - 1}
            >
              <Tooltip titlesAndTexts={generateTooltipText(item)}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "space-between",
                    paddingLeft: 1,
                  }}
                >
                  <Typography variant="h4" {...labelStyle}>
                    {item}
                  </Typography>
                  <IconButton
                    aria-label={`Delete ${item}`}
                    onClick={() => handleItemRemove(item)}
                  >
                    <Delete sx={{ color: "#755FFA" }} />
                  </IconButton>
                </Box>
              </Tooltip>
            </CompactListItem>
          ))}
        </List>
      )}
      <IconButton
        aria-label={label}
        onClick={handleMenuOpen}
        sx={{ paddingLeft: 0 }}
      >
        <AddCircle sx={{ color: "#755FFA" }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectableItems.map(
          (item) =>
            !selectedItems.includes(item.name) && (
              <MenuItem
                aria-label={item.name}
                key={item.name}
                onClick={() => handleItemAdd(item.name)}
              >
                {item.name}
              </MenuItem>
            )
        )}
      </Menu>
    </>
  );
};

export default EditableCompactList;
